// The image/video renderer for the current gallery slot. Receives
// a blob URL + mime and renders the right element. Owns the zoom
// and pan transform for images. Forwards raw pointer events to
// the useGestures hook via callbacks from the parent.

import * as React from "react";

interface Props {
  url: string | null;
  mime: string | null;
  loading: boolean;
  error: string | null;
  zoom: number;
  panX: number;
  panY: number;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
}

export function MediaStage({
  url,
  mime,
  loading,
  error,
  zoom,
  panX,
  panY,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: Props) {
  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-white/70">
        {error}
      </div>
    );
  }
  if (loading || !url || !mime) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/80" />
      </div>
    );
  }
  const isVideo = mime.startsWith("video/");
  const transform = isVideo
    ? undefined
    : `translate(${panX}px, ${panY}px) scale(${zoom})`;

  return (
    <div
      className="flex h-full w-full touch-none items-center justify-center overflow-hidden"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {isVideo ? (
        <video
          src={url}
          controls
          playsInline
          className="max-h-full max-w-full"
        />
      ) : (
        <img
          src={url}
          alt=""
          draggable={false}
          style={{ transform, transformOrigin: "center center" }}
          className="max-h-full max-w-full select-none transition-none"
        />
      )}
    </div>
  );
}
